import { useRef, useState } from 'react';

import Modal from '../../../common/components/modal/modal';
import { Gate } from '../../../global/contexts/auth/auth';
import { useBatchOrders } from '../contexts/fleet';
import { onFixtures } from '../queries/source';
import { agentName, batchIntents, orderCandidates } from '../utils/fleet';

const RESULT_LABELS = {
    pending: 'Pendiente de envío', registered: 'Orden registrada', simulated: 'Simulado',
    uncertain: 'Sin respuesta confirmada', failed: 'No se pudo registrar',
};

function BatchConfirmation({ rows, verb, scope, repositories, onClose }) {
    const [snapshot] = useState(() => orderCandidates(rows, verb));
    const [selected, setSelected] = useState(() => new Set(snapshot.map(row => row.position_id)));
    const [entries, setEntries] = useState(null);
    const [reason, setReason] = useState('');
    const running = useRef(false);
    const issue = useBatchOrders();
    const isEnabling = verb === 'habilitar';
    const remaining = entries?.filter(entry => ['pending', 'uncertain'].includes(entry.state)).length;
    const hasUncertain = entries?.some(entry => entry.state === 'uncertain');
    const registered = entries?.filter(entry => entry.state === 'registered').length ?? 0;
    const toggle = id => setSelected(previous => {
        const next = new Set(previous);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const run = async () => {
        if (running.current) return;
        running.current = true;
        const requests = entries ?? batchIntents(snapshot.filter(row => selected.has(row.position_id)), verb, reason);
        setEntries(requests);
        try {
            setEntries(await issue.mutateAsync({ entries: requests, onProgress: setEntries }));
        } catch {
            // Unexpected transport failures are surfaced by the mutation below.
        } finally {
            running.current = false;
        }
    };
    return (
        <Modal open title={isEnabling ? 'Autorizar trabajo autónomo' : 'Pausar nuevas tareas'}
            busy={issue.isPending} onClose={onClose}
            actions={<>
                <button className="coord__button" type="button" disabled={issue.isPending} onClick={onClose}>
                    {entries ? 'Cerrar' : 'Cancelar'}
                </button>
                {(!entries || remaining > 0) && <button type="button" className="coord__button coord__button--primary"
                    disabled={issue.isPending || !selected.size} onClick={run}>
                    {issue.isPending ? 'Enviando…' : entries ? 'Continuar solicitudes pendientes' : onFixtures ? 'Simular solicitud' : `${isEnabling ? 'Autorizar' : 'Pausar'} ${selected.size} ${selected.size === 1 ? 'agente' : 'agentes'}`}
                </button>}
            </>}>
            <p className="coord-batch__scope"><strong>{scope}</strong></p>
            <p className="coord__panel-note">{isEnabling
                ? 'Estos agentes podrán desarrollar o revisar trabajo ya autorizado. Las aprobaciones de tareas, aceptación y despliegue mantienen sus reglas. Los agentes que registres después necesitarán autorización propia.'
                : 'Se pausa la toma de trabajo nuevo. Las tareas en ejecución pueden continuar.'}</p>
            {isEnabling && <p className="coord__panel-note">También puedes renovar una autorización anterior si el equipo se reinició y quedó pausado. Al confirmar se enviará una nueva orden a cada agente seleccionado.</p>}
            {rows.length > snapshot.length && <p className="coord__panel-note">Se omiten {rows.length - snapshot.length} agentes {isEnabling
                ? 'sin vínculo vigente o sin las revisiones necesarias para operar.'
                : 'que ya confirmaron la pausa o cuyo vínculo no permite operar.'}</p>}
            {!entries && <label className="coord-batch__choice">
                <input type="checkbox" checked={selected.size === snapshot.length && snapshot.length > 0}
                    onChange={event => setSelected(new Set(event.target.checked ? snapshot.map(row => row.position_id) : []))} />
                Seleccionar todos ({snapshot.length})
            </label>}
            <ul className="coord-batch__list" aria-label="Agentes incluidos en la solicitud">
                {(entries ?? snapshot.map(row => ({ row, positionId: row.position_id }))).map(entry => <li key={entry.positionId}>
                    <label className="coord-batch__choice">
                        {!entries && <input type="checkbox" checked={selected.has(entry.positionId)} onChange={() => toggle(entry.positionId)} />}
                        <span><strong>{agentName(entry.row, repositories)}</strong><small>Agente #{entry.positionId} · {entry.row.machine.name}</small>
                            {entry.row.presence.state !== 'activa' && <small>Sin conexión confirmada; debe reconectar antes de que venza la orden.</small>}
                            {entry.state && <small>{RESULT_LABELS[entry.state]}</small>}
                            {entry.error && <small className="coord__error">{entry.error}</small>}
                        </span>
                    </label>
                </li>)}
            </ul>
            {!entries && <label className="coord__field"><span>Motivo (opcional)</span>
                <textarea className="coord__textarea" rows={2} maxLength={2000} value={reason} onChange={event => setReason(event.target.value)} />
            </label>}
            <p className="coord__panel-note">La orden vence en 30 minutos si el equipo no la aplica. Autorizar y recibir la confirmación son pasos distintos.</p>
            {entries && <p role="status" className="coord__success">{onFixtures
                ? 'Modo demostración: no se modifica Mastermind.'
                : `${registered} de ${entries.length} órdenes registradas. La confirmación de cada agente aparecerá en el panel.`}</p>}
            {hasUncertain && <p role="alert">Se detuvo el envío por una respuesta incierta. Continuar reutiliza las mismas solicitudes; no duplica las órdenes registradas.</p>}
            {entries?.some(entry => entry.state === 'failed') && <p role="alert">Algunas órdenes fallaron. Actualiza el panel antes de volver a decidir sobre esos agentes.</p>}
            {issue.error && <p role="alert" className="coord__error">{issue.error.message}</p>}
        </Modal>
    );
}

export default function BatchModal(props) {
    return <Gate all={['machines:read', 'machines:admin', ...(props.verb === 'habilitar' ? ['tokens:admin'] : [])]}>
        <BatchConfirmation {...props} />
    </Gate>;
}
